"use client";

import React, { useEffect, useState } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import styles from "./index.module.css";
import pathwayStyles from "../variants/Pathway/index.module.css";
import titleTopStyles from "../variants/TitleTop/index.module.css";
import titleLeftStyles from "../variants/TitleLeft/index.module.css";
import valueOrientedContentStyles from "../variants/ValueOrientedContent/index.module.css";
import Icon from "@/components/icons";
import { IconTypes } from "@/components/icons/types";

type Props = {
  children?: React.ReactNode;
  trigger: string | React.ReactNode;
  value?: string;
  defaultOpen?: boolean;
  variant?:
    | "default"
    | "pathway"
    | "pathway-dark"
    | "title-left"
    | "title-top"
    | "value-oriented-content"
    | undefined;
  pathWayIconType?: IconTypes;
};

export default function AccordionTextItem({
  children,
  trigger,
  value,
  defaultOpen = false,
  variant,
  pathWayIconType,
}: Props) {
  const [isHydrated, setIsHydrated] = useState(false);

  // On client-side hydration, hide content if not defaultOpen
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  let variantStyles;
  switch (variant) {
    case "pathway-dark":
    case "pathway":
      variantStyles = pathwayStyles;
      break;
    case "title-top":
      variantStyles = titleTopStyles;
      break;
    case "title-left":
      variantStyles = titleLeftStyles;
      break;
    case "value-oriented-content":
      variantStyles = valueOrientedContentStyles;
      break;
    default:
      variantStyles = styles;
      break;
  }

  const getValue = () => {
    if (value) return value;
    if (typeof trigger === "string") return trigger;
    return Math.random().toString(36);
  };

  // For SSR/SEO: show content by default
  // After hydration: use Radix UI behavior
  if (!isHydrated) {
    return (
      <details open className={`${styles.item} ${variantStyles.item ?? ""}`}>
        <summary
          className={`${styles.trigger} ${variantStyles.trigger ?? ""} cursor-pointer list-none [&::-webkit-details-marker]:hidden`}
        >
          <div className="flex items-center w-full relative">
            {(variant === "pathway" || variant === "pathway-dark") && (
              <div className="absolute top-[15px] left-[15px]">
                <div
                  className={`flex justify-center items-center h-9 w-9 border-2 border-primary rounded-full ${
                    variant === "pathway-dark"
                      ? "bg-primary text-white"
                      : "bg-info-light"
                  }`}
                >
                  <Icon
                    type={pathWayIconType || "library-books"}
                    pathProps={{
                      fill:
                        variant === "pathway-dark" ? "white" : "currentcolor",
                      height: 16,
                      width: 16,
                    }}
                  />
                </div>
              </div>
            )}
            {typeof trigger === "string" ? <span>{trigger}</span> : trigger}
          </div>
        </summary>
        <div className={`${styles.content} ${variantStyles.content ?? ""}`}>
          {children}
        </div>
      </details>
    );
  }

  // After hydration: use Radix UI
  return (
    <AccordionItem
      value={getValue()}
      className={`${styles.item} ${variantStyles.item ?? ""}`}
    >
      <AccordionTrigger
        className={`${styles.trigger} ${variantStyles.trigger ?? ""}`}
      >
        {(variant === "pathway" || variant === "pathway-dark") && (
          <div className="absolute top-[15px] left-[15px]">
            <div
              className={`flex justify-center items-center h-9 w-9 border-2 border-primary rounded-full ${
                variant === "pathway-dark"
                  ? "bg-primary text-white"
                  : "bg-info-light"
              }`}
            >
              <Icon
                type={pathWayIconType || "library-books"}
                pathProps={{
                  fill: variant === "pathway-dark" ? "white" : "currentcolor",
                  height: 16,
                  width: 16,
                }}
              />
            </div>
          </div>
        )}
        {typeof trigger === "string" ? <span>{trigger}</span> : trigger}
      </AccordionTrigger>
      <AccordionContent
        className={`${styles.content} ${variantStyles.content ?? ""}`}
      >
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}
